// Wire types for the Rules admin surface. Field names are snake_case to
// match the backend JSON contract.
//
// Per LLD 11 v0.2: rules now carry two independent scope axes
// (`location_scope` and `device_scope`), each a `{ mode, ids }` shape with
// mode ∈ {ALL, ALLOW, DENY}. The legacy `all_tenant_locations` /
// `fixed_list` modes have been retired; deny-wins composition replaces
// the old priority-based ordering, and `priority` is no longer surfaced
// in the form.

import type { AuditEntry, ReconcileSummary } from "./role";

export type { ReconcileSummary, AuditEntry };

export interface GroupMatch {
  type: "exact";
  group_name: string;
}

/** Per-axis scope mode (LLD 11 §7). */
export type ScopeMode = "ALL" | "ALLOW" | "DENY";

export interface LocationScope {
  mode: ScopeMode;
  /** Empty when mode === "ALL"; otherwise non-empty (max 200 ids). */
  ids: string[];
}

// ---------------------------------------------------------------------------
// LLD 12 — device name-predicate scope (Phase 1).
//
// The device axis keeps mode ∈ {ALL, ALLOW, DENY} (the *effect*) and gains a
// second, mutually-exclusive *selector*: a scope names its devices by EITHER an
// explicit `ids` list OR a typed `predicate` over device attributes. There is
// NO new mode — `predicate` attaches to ALLOW/DENY only (ALLOW = match-to-grant,
// DENY = match-to-deny). `location_scope` does NOT carry a predicate (device
// axis only).
//
// Field names are snake_case to match the backend JSON contract, e.g.:
//   "device_scope": { "mode": "ALLOW", "predicate": { "join": "ANY_OF",
//       "conditions": [ { "field": "NAME", "op": "CONTAINS",
//                         "value": "break", "case_insensitive": true } ] } }
// ---------------------------------------------------------------------------

/** Attribute a condition matches on. `NAME` at v1; `TAG` reserved for the
 *  REB-owned Devices inventory (LLD 12 §8) and rejected at write until it
 *  ships — surfaced disabled in the builder. */
export type MatchField = "NAME" | "TAG";

/** Closed operator set (no regex). `GLOB` allows `*`/`?` only. */
export type MatchOp = "CONTAINS" | "PREFIX" | "SUFFIX" | "EXACT" | "GLOB";

/** How the flat condition list composes — one AND/OR toggle, no nesting. */
export type MatchJoin = "ALL_OF" | "ANY_OF";

/** One condition row: "<field> <op> <value>", optionally negated
 *  ("does not <op>") and case-insensitive (default true). */
export interface MatchCondition {
  field: MatchField;
  op: MatchOp;
  value: string;
  /** Per-condition negation ("does NOT <op> value"); default false. Distinct
   *  from `mode === "DENY"` (the effect). */
  negate?: boolean;
  /** Default true (Locale.ROOT lowercase fold; matches the camera-grid search
   *  box). */
  case_insensitive?: boolean;
  /** UI-only stable identity for React list keys while a row is edited/removed.
   *  Never sent on the wire — stripped when the predicate is built. */
  cid?: string;
}

/** A typed predicate over device attributes: a flat list of conditions joined
 *  by one ALL_OF (AND) / ANY_OF (OR) toggle. */
export interface ScopePredicate {
  join: MatchJoin;
  conditions: MatchCondition[];
}

export interface DeviceScope {
  mode: ScopeMode;
  /** Present for the `ids` selector; empty when mode === "ALL" or when the
   *  `predicate` selector is used (max 200 ids). */
  ids: string[];
  /** Present iff this scope selects devices by predicate (LLD 12). Mutually
   *  exclusive with a non-empty `ids`, and valid only with mode ∈
   *  {ALLOW, DENY}. */
  predicate?: ScopePredicate;
}

/** Max conditions per predicate axis (LLD 12 §3.7 `MAX_MATCH_CONDITIONS_PER_AXIS`). */
export const MAX_MATCH_CONDITIONS_PER_AXIS = 16;

/** A `CONTAINS` value shorter than this triggers the inline over-match
 *  warning (LLD 12 §4.3 / D-5). */
export const CONTAINS_OVERMATCH_MIN_LENGTH = 4;

/** Max `value` length per condition (LLD 12 §4.3). */
export const MATCH_VALUE_MAX_LENGTH = 128;

export interface Rule {
  rule_id: string;
  tenant_id: string;
  display_name: string;
  description: string;
  /**
   * Group binding for the rule. Declared optional because the wire can omit
   * it for group-less / edge rules (and pre-v0.2 BFFs that never synthesized
   * the nested shape). All read sites MUST optional-chain
   * (`rule.group_match?.group_name ?? "—"`) — a bare `.group_name` deref on an
   * undefined value throws and blanks the whole SPA.
   */
  group_match?: GroupMatch;
  role_id: string;
  location_scope: LocationScope;
  device_scope: DeviceScope;
  enabled: boolean;
  version: number;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
  deleted_at?: string;
}

export interface RuleUser {
  alias: string;
  ring_user_id: string;
  location_id: string;
  granted_at: string;
}

export interface GroupMember {
  alias: string;
  ring_user_id: string;
  group_name: string;
  synced_at: string;
}

export interface ValidateRuleRequest {
  rule_id?: string;
  display_name?: string;
  description?: string;
  group_match?: GroupMatch;
  role_id?: string;
  location_scope?: LocationScope;
  device_scope?: DeviceScope;
  enabled?: boolean;
}

/** Max ids per scope axis (LLD 11 §12.2). Form validation enforces. */
export const MAX_SCOPE_IDS_PER_RULE = 200;
